import { ResourceModel } from '@/models/content/resource';
import { taxonomySerializer } from './taxonomy';
import { sessionService } from '@/providers/services/auth/session';
import { DEFAULT_IMAGES_PATH } from '@/utils/constants';
import { addProtocolIfNecessary } from '@/utils/question';
import { gutCourseNames, truncateString } from '@/utils/utils';
import moment from 'moment';

export class ResourceSerializer {
  private static INSTANCE = new ResourceSerializer();

  static get instance() {
    return this.INSTANCE;
  }

  public serializeResource(resource: any): ResourceModel {
    const cdnUrls = sessionService.getCdnUrl();
    const contentCdnUrl = cdnUrls.content_cdn_url;
    const thumbnailUrl = resource.thumbnail ?
      contentCdnUrl + resource.thumbnail :
      `${window.location.origin}/` + DEFAULT_IMAGES_PATH.unit;
    const result: ResourceModel = {
      id: resource.id,
      title: resource.title,
      description: resource.description,
      subformat: resource.contentSubFormat,
      format: resource.format || 'resource',
      efficacy: resource.efficacy,
      relevance: resource.relevance,
      engagement: resource.engagement,
      standards: taxonomySerializer.normalizeTaxonomyObject(resource.taxonomy),
      creator: taxonomySerializer.normalizeCreator(resource.creator),
      isVisibleOnProfile: false,
      thumbnailUrl,
    };
    return result;
  }

  /**
   * Normalize the resource data into a Resource object
   * @param resourceData
   * @returns {Resource}
   */
  public normalizeReadResource(resourceData: any) {
    const serializer = this;
    const format = resourceData.content_format
      ? resourceData.content_format.split('_')[0] : undefined;
    const standards = resourceData.taxonomy || {};
    const cdnUrls = sessionService.getCdnUrl();
    const cdnUrl = cdnUrls.content_cdn_url;
    const info = resourceData.info || {};
    const metadata = resourceData.metadata || {};

    const resource: any = {
      id: resourceData.id,
      title: resourceData.title,
      url: resourceData.url,
      creatorId: resourceData.creator_id,
      format,
      content_format: resourceData.content_format,
      description: resourceData.description,
      narration: resourceData.narration,
      publishStatus: resourceData.publish_status,
      originalResourceId: resourceData.original_content_id,
      standards: taxonomySerializer.normalizeTaxonomyObject(standards),
      owner: resourceData.creator_id,
      info,
      amIThePublisher: resourceData.is_copyright_owner || false,
      publisher: resourceData.copyright_owner
        ? resourceData.copyright_owner[0]
        : null,
      isVisibleOnProfile:
        typeof resourceData.visible_on_profile !== 'undefined'
          ? resourceData.visible_on_profile
          : true,
      order: resourceData.sequence_id,
      displayGuide:
        resourceData.display_guide &&
        (resourceData.display_guide.is_broken === 1 ||
          resourceData.display_guide.is_frame_breaker === 1),
      isRemote: resourceData.is_remote !== false, // if true or undefined should be true
      centurySkills:
        metadata['21_century_skills'] &&
          metadata['21_century_skills'].length > 0
          ? metadata['21_century_skills']
          : [],
    };

    if (resourceData.url) {
      const protocolPattern = /^((http|https|ftp):\/\/)/;
      if (!protocolPattern.test(resourceData.url)) {
        // if no protocol add it
        let resourceUrl = resourceData.url;
        const containsCdnUrl = resourceUrl.indexOf(cdnUrl) !== -1;
        if (!containsCdnUrl) {
          resourceUrl = cdnUrl + resourceUrl;
        }
        resource.url = addProtocolIfNecessary(resourceUrl, true);
      }
      const url = resource.url.split('/');
      resource.fileName = url[url.length - 1];
    }
    return resource;
  }

  /**
   * Normalized data of resource by id
   * @return {Object}
   */
  public normalizeResourceContent(response: any) {
    const thumbnail = response.thumbnail;
    const cdnUrls = sessionService.getCdnUrl();
    if (!thumbnail) {
      response.thumbnail = DEFAULT_IMAGES_PATH.profile;
    } else {
      response.thumbnail = cdnUrls.user + thumbnail;
    }
    const taxonomy = taxonomySerializer.normalizeTaxonomyObject(response.taxonomy);
    response.taxonomy = taxonomy[0];
    return response;
  }

  public resourcePulloutSerializer(collection: any, selectedResource: any,
                                   metadataLevels: any, summary: any, transcript: any) {
    let resultSet = {};
    const defaultVectorValue = 0.5;
    if (collection) {
      const advertisementLevel = metadataLevels.advertisement_level || {};
      const audience = metadataLevels.audience || {};
      const grade = metadataLevels.grade || {};
      const License = metadataLevels.license || {};
      const educationalUse = metadataLevels.educational_use || {};
      const gradeLevelText: any = [];
      const info = collection.info || null;
      const collectionGrade = collection.metadata
        ? collection.metadata.grade
        : null;
      if (collectionGrade) {
        collectionGrade.forEach((gradeLevel: any) => {
          gradeLevelText.push(grade[gradeLevel]);
        });
      }
      resultSet = {
        'descriptive': {
          'title': collection.title,
          'description': truncateString(collection.description),
          'Published By': (info && info.publisher) ? info.publisher.toString() : 'Gooru org',
          'Published Status': 'Published',
          'address': collection.url ? collection.url : 'No',
          'License': collection.license
            ? License[collection.license]
            : 'Public Domain',
          'created by': collection.owner.username,
          'created on': collection.publish_date
            ? moment(collection.publish_date).format('MMMM DD, YYYY')
            : null,
          'modified on': collection.modeified_date
            ? collection.modeified_date
            : null,
          'modified by': collection.modified_by,
        },

        'instructional': {
          'subject': selectedResource.taxonomySubject,
          'course': selectedResource.taxonomyCourse
            ? gutCourseNames(selectedResource.taxonomyCourse).join(', ')
            : null,
          'domain': selectedResource.taxonomyDomain
            ? selectedResource.taxonomyDomain.join(', ')
            : null,
          'standard': selectedResource.standards ? selectedResource.standards : null,
          'audience': collection.metadata
            ? collection.metadata.audience
              ? audience[collection.metadata.audience]
              : null
            : null,
          'Depth of Knowledge': collection.depthofknowledge.map((item: any) => item.label).toString(),
          'Reading Level': collection.readingLevel.map((item: any) => item.label).toString(),
          '21st Century Skills': collection.skills,
          'language': collection.info
            ? collection.info.language === 'eng'
              ? 'English'
              : !collection.info.language
                ? 'English'
                : collection.info.language
            : 'English',
          'educational use': collection.metadata
            ? educationalUse[collection.metadata.educational_use]
            : null,
          'grade': collection.metadata
            ? collection.metadata.grade
              ? gradeLevelText.join(', ')
              : null
            : null,
          'age-range': null,
          'Editorial Range': selectedResource.publisherQualityIndicator,
          'keywords': collection.info
            ? collection.info.keywords
              ? collection.info.keywords[0]
              : null
            : null,
        },

        'content analysis': {
          'summary': summary,
          'transcript': transcript,
          'accessbility': collection.accessibility,
          'format': collection.content_subformat
            ? collection.content_subformat.replace(/_/g, ' ')
            : null,
          'media Features': collection.media ? collection.media : null,
          'access hazard': collection.accesshazard
            ? collection.accesshazard
            : 'None',
          'advertisement level': collection.metadata
            ? collection.metadata.advertisement_level
              ? advertisementLevel[collection.metadata.advertisement_level]
              : 'Low'
            : 'Low',
          'framebreaker': collection.display_guide
            ? collection.display_guide.is_frame_breaker === 1
              ? 'Yes'
              : 'No'
            : 'No',
          // 'Is Broken': collection.publish_date.is_broken
          //   ? collection.publish_date.is_broken
          //   : 'No',
        },
        'efficacy': {
          relevance: selectedResource.relevance || defaultVectorValue,
          engagement: selectedResource.engagement || defaultVectorValue,
          efficacy: selectedResource.efficacy || defaultVectorValue,
        },
      };
    }
    return resultSet;

  }

}

export const resourceSerializer = ResourceSerializer.instance;
